import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, ApiError } from "../auth/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";

type Status = "verifying" | "ok" | "expired" | "invalid";

export function VerifyEmail() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const { verifyEmail } = useAuth();
  const isMobile = useIsMobile();
  const cfg = useSiteConfig();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState<Status>("verifying");
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return; // evita doppia esecuzione (StrictMode)
    ranRef.current = true;
    (async () => {
      if (!token) {
        setStatus("invalid");
        return;
      }
      try {
        await verifyEmail(token);
        setStatus("ok");
      } catch (err) {
        if (err instanceof ApiError && err.message === "token_expired") setStatus("expired");
        else setStatus("invalid");
      }
    })();
  }, [token, verifyEmail]);

  const lang = locale || "it";
  const title =
    status === "verifying" ? t("auth.verifying")
    : status === "ok" ? t("auth.verifiedTitle")
    : t("auth.verifyFailedTitle");
  const body =
    status === "verifying" ? ""
    : status === "ok" ? t("auth.verifiedBody")
    : status === "expired" ? t("auth.verifyExpired")
    : t("auth.verifyInvalid");

  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "48px 20px" : "64px 24px",
        background: cfg.color_light_bg,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: cfg.color_body_bg,
          borderRadius: 8,
          padding: isMobile ? "36px 24px" : "48px 40px",
          border: "1px solid rgba(28,75,59,0.89)",
          textAlign: "center",
        }}
      >
        <div style={{ marginBottom: 24 }}>
          {cfg.logo_url ? (
            <img
              src={resolveImageUrl(cfg.logo_url)}
              alt={cfg.brand_name}
              style={{ height: 36, objectFit: "contain" }}
            />
          ) : (
            <div style={{ fontSize: 20, color: cfg.color_dark, fontFamily: cfg.font_heading, letterSpacing: 1 }}>
              {cfg.brand_name}
            </div>
          )}
        </div>

        <h1
          style={{
            fontSize: isMobile ? 22 : 26,
            fontWeight: 400,
            margin: "0 0 12px",
            color: cfg.color_dark,
            fontFamily: cfg.font_heading,
          }}
        >
          {title}
        </h1>

        {body && (
          <p style={{ fontSize: 14, color: "rgba(28,75,59,0.86)", lineHeight: 1.7, margin: "0 0 24px" }}>
            {body}
          </p>
        )}

        {status !== "verifying" && (
          <Link
            to={`/${lang}/login`}
            style={{
              display: "inline-block",
              background: cfg.color_dark,
              color: "#fff",
              textDecoration: "none",
              padding: "12px 28px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: 1,
              textTransform: "uppercase",
            }}
          >
            {t("auth.goToLogin")}
          </Link>
        )}
      </div>
    </div>
  );
}
